import * as React from "react";
import { useState } from "react";
import { BoxAd, ImmersiveAd } from "@bloomberg/media-ui-ads";
import { createClasses } from "@bloomberg/media-ui-core";
import SparkleAd from "@foundation/sparkle/lib/SparkleAd";

import { PageContext } from "~context/PageContext";

import BrandMetrics from "./brandMetrics";
import { getAdOptions } from "./utility";
import styles from "./ad.module.css";

interface AdProps {
    pageId: string;
    positionIncrement?: number;
    type: string;
}

interface LineupAdProps extends AdProps {
    useGrid?: boolean;
}

const LineupAd: React.FC<LineupAdProps> = ({
    pageId,
    positionIncrement,
    type,
    useGrid,
}) => {
    const [adRendered, setAdRendered] = useState(false);
    if (type === "brandMetrics") return <BrandMetrics />;

    const positionSuffix = positionIncrement ? `-${positionIncrement}` : "";
    const adId = `${type}-${pageId}${positionSuffix}`;
    const adOptions = getAdOptions(type, pageId, positionIncrement);
    const onRender = () => {
        setAdRendered(true);
    };
    return (
        <div
            className={createClasses(styles.wrapper, styles[type], {
                [styles.rendered]: adRendered,
                [styles.useGrid]: useGrid,
            })}
            data-component="Ad"
        >
            <SparkleAd id={adId} options={adOptions} onRender={onRender} />
        </div>
    );
};

const Ad = (props: AdProps) => {
    const { useGrid } = React.useContext(PageContext);

    const { type, positionIncrement } = props;

    // override dimensions defined in media-ui-ads
    const mobileImmersizeAdOptions = {
        dimensions: {
            all: [
                [300, 250],
                [3, 3],
            ],
        },
    };

    const newAdEl = (() => {
        switch (type) {
            case "newDesktopBoxStandard":
                return (
                    <BoxAd
                        positionIncrement={positionIncrement}
                        isDesktop
                        className={createClasses([
                            styles[type],
                            useGrid && styles.useGrid,
                            useGrid && styles.newDesktopBoxStandardGrid,
                        ])}
                    />
                );
            case "newDesktopBoxFlex":
                return (
                    <BoxAd
                        positionIncrement={positionIncrement}
                        flexSizeAllowed
                        className={createClasses([
                            styles[type],
                            useGrid && styles.useGrid,
                        ])}
                        showPlaceholder={false}
                    />
                );
            case "newDesktopImmersive":
                return (
                    <ImmersiveAd
                        positionIncrement={positionIncrement}
                        isDesktop
                        className={createClasses([
                            styles[type],
                            useGrid && styles.useGrid,
                        ])}
                        showPlaceholder={false}
                    />
                );
            case "newTabletImmersive":
                return (
                    <ImmersiveAd
                        positionIncrement={positionIncrement}
                        isTablet
                        className={createClasses([
                            styles[type],
                            useGrid && styles.useGrid,
                        ])}
                        showPlaceholder={false}
                    />
                );
            case "newMobileImmersive":
                return (
                    <ImmersiveAd
                        positionIncrement={positionIncrement}
                        isMobile
                        className={createClasses([
                            styles[type],
                            useGrid && styles.useGrid,
                        ])}
                        showPlaceholder={false}
                        adOptions={mobileImmersizeAdOptions}
                    />
                );
            case "newMobileBox":
            case "programmaticMobile":
                return (
                    <ImmersiveAd
                        positionIncrement={positionIncrement}
                        isMobile
                        className={createClasses([
                            styles.newMobileBox,
                            useGrid && styles.useGrid,
                        ])}
                        showPlaceholder={false}
                    />
                );
            default:
                return undefined;
        }
    })();

    if (newAdEl) return newAdEl;

    return <LineupAd {...props} useGrid={useGrid} />;
};

export default Ad;
